import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/CheckBox";
import {
  useGetTagsQuery,
  useCreateTagMutation,
  TagsApi,
} from "@/services/TagsApi";
import { toast } from "@/components/ui/UseToast";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Building2,
  Folder,
  Wrench,
} from "lucide-react";
import {
  TagListItem,
  TagCreatePayload,
  TagWithMetadata,
  TagTree,
  TagType,
} from "@/app/types/tags";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/app/store";
import { createTagTreeFromTagNodes } from "@/utils/Helper";

const typeIconMap: Record<TagType, string> = {
  branch: "fa-solid fa-building",
  department: "fa-solid fa-folder",
  service: "fa-solid fa-wrench",
};

import { useSelector } from "react-redux";
import { RootState } from "@/app/store";

const { VITE_ORG_ID } = import.meta.env;

interface TagImportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  eventId: number;
  refetch: () => void;
}

const TagImportModal = ({
  open,
  setOpen,
  eventId,
  refetch,
}: TagImportModalProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [isImporting, setIsImporting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const ORG_ID = Number(userInfo?.org_id || VITE_ORG_ID || 1);

  const { data: globalTagsResponse, isLoading } = useGetTagsQuery(
    {
      org_id: ORG_ID,
    },
    { skip: !open },
  );

  const allTags = globalTagsResponse?.data ?? [];
  const tagTree = React.useMemo(
    () => createTagTreeFromTagNodes(allTags),
    [allTags],
  );
  const [createTag] = useCreateTagMutation();

  const handleToggleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const traverseNodes = (
    nodes: TagTree[],
    callback: (node: TagTree) => void,
  ) => {
    nodes.forEach((node) => {
      callback(node);
      if (node.children.length > 0) {
        traverseNodes(node.children, callback);
      }
    });
  };

  const handleToggleSelect = (node: TagTree) => {
    const idsToToggle: number[] = [];
    traverseNodes([node], (n) => idsToToggle.push(n.tag_id));

    const isSelected = selectedIds.includes(node.tag_id);
    if (isSelected) {
      setSelectedIds((prev) => prev.filter((id) => !idsToToggle.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...idsToToggle])]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === allTags.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allTags.map((t) => t.tag_id));
    }
  };

  const handleImport = async () => {
    if (selectedIds.length === 0) return;
    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    const idMap = new Map<number, number>();

    try {
      // 1. Fetch details for all selected tags
      const selectedTags: TagWithMetadata[] = [];
      for (const id of selectedIds) {
        try {
          const detail: any = await (dispatch as any)(
            TagsApi.endpoints.getTagById.initiate(id),
          );
          if (detail.data) selectedTags.push(detail.data);
        } catch (err) {
          console.error(`Failed to fetch details for tag ${id}`, err);
          errorCount++;
        }
      }

      // 2. Sort tags by path length to ensure parents are processed first
      selectedTags.sort(
        (a, b) => (a.path?.length || 0) - (b.path?.length || 0),
      );

      // 3. Import tags sequentially to build the new hierarchy
      for (const tagDetail of selectedTags) {
        try {
          // Map old path to new path using previously imported parents
          const oldParentPath = tagDetail.path.slice(0, -1);
          const newParentPath = oldParentPath
            .map((oldId) => idMap.get(oldId))
            .filter((id): id is number => id !== undefined);

          const newParentId =
            newParentPath.length > 0
              ? newParentPath[newParentPath.length - 1]
              : null;

          const payload: TagCreatePayload = {
            name: `(Copied) ${tagDetail.name}`,
            code: tagDetail.code ? `${tagDetail.code}_copied` : "",
            type: tagDetail.type,
            event_id: eventId,
            org_id: ORG_ID,
            tag_status: tagDetail.status === "active",
            path: newParentPath,
            parent_tag_id: newParentId,
            metadata: tagDetail.metadata as any,
          };

          const result: any = await createTag(payload).unwrap();
          if (result && result.tag_id) {
            idMap.set(tagDetail.tag_id, result.tag_id);
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to import tag ${tagDetail.tag_id}:`, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          variant: "success",
          title: "Import Complete",
          description: `Successfully imported ${successCount} business units.${errorCount > 0 ? ` Failed to import ${errorCount} units.` : ""}`,
        });
        refetch();
        setOpen(false);
        setSelectedIds([]);
      } else if (errorCount > 0) {
        toast({
          variant: "destructive",
          title: "Import Failed",
          description: `Failed to import ${errorCount} units.`,
        });
      }
    } finally {
      setIsImporting(false);
    }
  };

  const renderNode = (node: TagTree, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded[node.tag_id] ?? false;
    const isSelected = selectedIds.includes(node.tag_id);
    const Icon = typeIconMap[node.type];

    return (
      <div key={node.tag_id} className="w-full">
        <div
          className="flex items-center py-1 hover:bg-muted/50 rounded-sm group transition-colors"
          style={{ paddingLeft: `${depth * 1.5}rem` }}
        >
          <div className="w-6 flex items-center justify-center">
            {hasChildren && (
              <button
                onClick={() => handleToggleExpand(node.tag_id)}
                className="text-muted-foreground hover:text-foreground transition-transform duration-200"
                style={{
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            )}
          </div>
          <Checkbox
            id={`tag-${node.tag_id}`}
            checked={isSelected}
            onCheckedChange={() => handleToggleSelect(node)}
            className="mr-2"
          />
          <i
            className={`${typeIconMap[node.type]} mr-2 text-muted-foreground text-[10px] w-4 text-center`}
          />
          <label
            htmlFor={`tag-${node.tag_id}`}
            className="text-sm cursor-pointer flex-1 truncate py-1"
          >
            {node.name}
            <span className="ml-2 text-[10px] text-muted-foreground font-mono bg-muted px-1 rounded">
              {node.code}
            </span>
          </label>
        </div>
        {hasChildren && isExpanded && (
          <div className="w-full">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Import Business Units</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2 min-h-[300px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Fetching business units...
              </p>
            </div>
          ) : tagTree.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Folder className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">
                No business units available to import.
              </p>
            </div>
          ) : (
            <div className="space-y-1 py-2">
              <div className="flex items-center space-x-2 border-b pb-3 mb-3 sticky top-0 bg-background z-10">
                <Checkbox
                  id="select-all"
                  checked={
                    selectedIds.length === allTags.length && allTags.length > 0
                  }
                  onCheckedChange={handleSelectAll}
                />
                <label
                  htmlFor="select-all"
                  className="text-sm font-semibold cursor-pointer"
                >
                  Select All ({allTags.length} units found)
                </label>
              </div>
              <div className="pb-4">
                {tagTree.map((node) => renderNode(node))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t">
          <Button
            className="w-32"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            className="w-32"
            onClick={handleImport}
            disabled={selectedIds.length === 0 || isImporting}
          >
            {isImporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TagImportModal;
